import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import activoClassificationService from '../../../services/activoClassificationService';
import { useActivoClassification } from '../../../contexts/ActivoClassificationContext';
import CategoryForm from '../../../components/Classification/CategoryForm';
import ClassificationExcelToolbar from '../../../components/Classification/ClassificationExcelToolbar';
import {
  ClassificationBulkDeleteModal,
  ClassificationBulkDeleteTd,
  ClassificationBulkDeleteTh,
  ClassificationBulkDeleteToolbar,
} from '../../../components/Classification/ClassificationBulkDeleteUi';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import ActivosClassificationNav from './ActivosClassificationNav';
import { useCanMutate } from '../../../hooks/useCanMutate';
import { useClassificationBulkDelete } from '../../../hooks/useClassificationBulkDelete';
import { useClassificationHierarchyForBulk } from '../../../hooks/useClassificationHierarchyForBulk';

const ActivosCategories = () => {
  const canMutate = useCanMutate();
  const { categories, loading, error, refetch } = useActivoClassification();
  const { hierarchy: fullHierarchy, reloadHierarchy } = useClassificationHierarchyForBulk(
    activoClassificationService,
    categories.length
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const filteredCategories = useMemo(
    () =>
      categories.filter(
        (category) =>
          category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          category.code.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [categories, searchTerm]
  );

  const bulk = useClassificationBulkDelete({
    variant: 'categories',
    rows: filteredCategories,
    hierarchy: fullHierarchy,
    deleteItem: (id) => activoClassificationService.deleteCategory(id),
    onDeleted: async () => {
      await refetch();
      await reloadHierarchy();
    },
    setToast,
    enabled: canMutate,
  });

  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    try {
      const response = await activoClassificationService.deleteCategory(categoryToDelete.id);
      if (response.success) {
        setIsDeleteModalOpen(false);
        setCategoryToDelete(null);
        setToast({ isOpen: true, message: 'Categoría eliminada', type: 'success' });
        await refetch();
      } else {
        setIsDeleteModalOpen(false);
        setToast({ isOpen: true, message: response.message || 'No se pudo eliminar', type: 'error' });
      }
    } catch (err) {
      setIsDeleteModalOpen(false);
      setToast({
        isOpen: true,
        message: err.response?.data?.message || 'Error al eliminar',
        type: 'error',
      });
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Cargando categorías…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Categorías (activos)</h1>
              <p className="text-gray-600 mt-1">Nivel 1 — clasificación exclusiva del módulo de activos</p>
            </div>
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              <ClassificationExcelToolbar
                variant="categories"
                onImported={refetch}
                setToast={setToast}
                apiService={activoClassificationService}
                filenameSuffix="activos"
                importTitleSuffix=" (activos)"
                exportRows={filteredCategories}
              />
              <button
                type="button"
                onClick={() => {
                  setEditingCategory(null);
                  setIsFormOpen(true);
                }}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
              >
                + Nueva categoría
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

          <nav className="text-sm text-gray-600 mb-4">
            <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
            <span className="mx-2">/</span>
            <Link to="/activos" className="hover:text-teal-600">Activos</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Configuración</span>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Clasificación</span>
            <span className="mx-2">/</span>
            <span className="text-gray-800">Categorías</span>
          </nav>

          <ActivosClassificationNav />

          <div className="mb-4">
            <input
              type="text"
              placeholder="Buscar por nombre o código…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        <ClassificationBulkDeleteToolbar bulk={bulk} />

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {filteredCategories.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {searchTerm ? 'No hay resultados' : 'No hay categorías. Cree la primera con el botón superior.'}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <ClassificationBulkDeleteTh bulk={bulk} />
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCategories.map((category) => (
                  <tr key={category.id} className="hover:bg-gray-50">
                    <ClassificationBulkDeleteTd bulk={bulk} row={category} />
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{category.code}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{category.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{category.description || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${category.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {category.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link
                        to={`/activos/configuracion/clasificacion/subcategorias?category=${category.id}`}
                        className="text-teal-600 hover:text-teal-900 mr-4"
                      >
                        Subcategorías
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingCategory(category);
                          setIsFormOpen(true);
                        }}
                        className="text-purple-600 hover:text-purple-900 mr-4"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setCategoryToDelete(category);
                          setIsDeleteModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-900"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <CategoryForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingCategory(null);
        }}
        category={editingCategory}
        onSuccess={() => refetch()}
        apiService={activoClassificationService}
      />

      <ClassificationBulkDeleteModal bulk={bulk} />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setCategoryToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar categoría"
        message={categoryToDelete ? `¿Eliminar la categoría "${categoryToDelete.name}" (${categoryToDelete.code})?` : ''}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
      />

      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default ActivosCategories;
